import { Controller } from "@hotwired/stimulus"
import QRCode from "qrcode"

export default class extends Controller {
    static targets = ["modal", "url", "qrcode", "closeButton", "downloadUrl"]
    static values = {
        url: String,
        delay: { type: Number, default: 300 }
    }

    connect() {
        this.hideTimeout = null
        this.showTimeout = null
        this.isVisible = false
        this.currentLink = null // 当前激活的链接

        // 只在桌面端启用 popover 功能
        if (this.isMobile()) {
            console.log('Popover: 移动设备检测，功能已禁用')
            return
        }

        this.setupGlobalListeners()
        console.log('Popover: 控制器已连接')
    }

    disconnect() {
        this.removeGlobalListeners()
        this.clearTimeouts()
    }

    // 设置全局监听器来检测带有 data-popover="modal" 的链接
    setupGlobalListeners() {
        this.handleMouseEnter = this.handleMouseEnter.bind(this)
        this.handleMouseLeave = this.handleMouseLeave.bind(this)

        // 使用事件委托来监听所有链接，使用冒泡的 mouseover/mouseout
        document.addEventListener('mouseover', this.handleMouseEnter, false)
        document.addEventListener('mouseout', this.handleMouseLeave, false)
    }

    removeGlobalListeners() {
        document.removeEventListener('mouseover', this.handleMouseEnter, false)
        document.removeEventListener('mouseout', this.handleMouseLeave, false)
    }

    // 检测是否为移动设备
    isMobile() {
        return window.innerWidth < 768 || ('ontouchstart' in window && navigator.maxTouchPoints > 1)
    }

    // 处理鼠标进入事件（使用 relatedTarget 去抖动）
    handleMouseEnter(event) {
        if (this.isMobile()) return

        if (!event.target || !event.target.closest) return

        const link = event.target.closest('a[data-popover="modal"]')
        if (!link) return

        // 从同一个链接的子元素移动到链接本身：忽略
        const from = event.relatedTarget
        if (from && link.contains(from)) return

        // 从 modal 移动到链接：允许显示
        if (this.isVisible && this.currentLink === link) return

        this.currentLink = link
        this.showWithUrl(link.href)
    }

    // 处理鼠标离开事件（使用 relatedTarget 去抖动）
    handleMouseLeave(event) {
        if (this.isMobile()) return

        if (!event.target || !event.target.closest) return

        const link = event.target.closest('a[data-popover="modal"]')
        if (!link) return

        const to = event.relatedTarget
        // 在同一链接内部移动：忽略
        if (to && link.contains(to)) return
        // 正在移动到 modal 上：忽略隐藏
        if (to && this.hasModalTarget && this.modalTarget.contains(to)) return

        // 离开该链接：隐藏
        if (this.currentLink === link) {
            this.currentLink = null
            this.hide()
        }
    }

    // 直接用 URL 显示（避免重复计算）
    showWithUrl(url) {
        if (this.isMobile()) return

        this.clearTimeouts()
        if (!url) return
        this.showTimeout = setTimeout(() => {
            this.displayModal(url)
        }, this.delayValue)
    }

    // 鼠标进入链接时显示模态框（保留给非委托场景）
    show(event) {
        if (this.isMobile()) return

        this.clearTimeouts()
        const link = event?.target?.closest ? event.target.closest('a[data-popover="modal"]') : null
        const url = link ? link.href : this.urlValue
        if (!url) return
        this.showTimeout = setTimeout(() => {
            this.displayModal(url)
        }, this.delayValue)
    }

    // 鼠标离开链接时隐藏模态框
    hide() {
        this.clearTimeouts()
        this.hideTimeout = setTimeout(() => {
            this.hideModal()
        }, 150)
    }

    // 鼠标进入模态框时保持显示
    keepVisible() {
        this.clearTimeouts()
    }

    // 鼠标离开模态框时隐藏
    hideOnLeave() {
        this.clearTimeouts()
        this.hideTimeout = setTimeout(() => {
            this.hideModal()
        }, 200)
    }

    // 点击关闭按钮
    close() {
        this.hideModal()
    }

    // 点击背景关闭
    closeOnBackdrop(event) {
        if (event.target === event.currentTarget) {
            this.hideModal()
        }
    }

    // 复制链接到剪贴板
    copyUrl() {
        const url = this.urlTarget.textContent
        navigator.clipboard.writeText(url).then(() => {
            this.showCopyFeedback()
        }).catch(err => {
            console.error('复制失败:', err)
        })
    }

    // 显示复制成功反馈
    showCopyFeedback() {
        const button = this.urlTarget.parentElement.querySelector('button')
        const originalText = button.textContent
        button.textContent = '已复制!'
        button.classList.add('bg-green-500')

        setTimeout(() => {
            button.textContent = originalText
            button.classList.remove('bg-green-500')
        }, 2000)
    }

    // 私有方法
    displayModal(url) {
        if (this.isMobile()) return

        if (this.isVisible) return

        this.isVisible = true

        // 更新 URL 和生成二维码
        if (this.hasUrlTarget) {
            this.urlTarget.href = url
            this.downloadUrlTarget.href = url
            this.urlTarget.textContent = url
        }

        this.generateQRCode(url)

        // 显示 popover modal
        if (this.hasModalTarget) {
            this.modalTarget.classList.remove('hidden')
        }
    }

    hideModal() {
        if (!this.isVisible) return

        this.isVisible = false
        this.currentLink = null

        // 隐藏 popover modal
        if (this.hasModalTarget) {
            this.modalTarget.classList.add('hidden')
        }
    }


    generateQRCode(url) {
        QRCode.toDataURL(url, {
            width: 200,
            height: 200,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            }
        }).then(dataUrl => {
            if (this.hasQrcodeTarget) {
                this.qrcodeTarget.src = dataUrl
            }
        }).catch(err => {
            console.error('二维码生成失败:', err)
        })
    }

    clearTimeouts() {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout)
            this.showTimeout = null
        }
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout)
            this.hideTimeout = null
        }
    }
}
